<template>
  <div v-loading="isProductLoading" class="product-detail text-gray-600">
    <div class="container no-padding-container">
      <BreadCrumbsComponent
        class="pt-7 pl-5 pb-5 md:pl-0 md:pt-6 md:pb-16"
        :breadcrumbs="breadcrumbs"
        @click="onBreadCrumbClick"
      />
      <div class="product-detail-container">
        <ProductPhotoGallery
          :product-slug="product.slug"
          :amount-of-photos-to-show="product.numberOfPhotosToShow || 4"
        />
        <div
          v-loading="isProductLoading"
          class="product-detail-body bg-white relative px-5 mt-5 opacity-0 transition-opacity ease-in duration-500 delay-200"
          :class="{ 'opacity-100': !isProductLoading }"
        >
          <div class="product-detail-main-info flex flex-col mb-7">
            <h1 class="product-detail-title mb-3" v-text="product.name" />
            <div class="mt-auto">
              <span class="ota-p18 block mb-3" v-text="product.price ? toEuros(product.price, true): ''" />
              <ProductCreditBadge
                :credits="spaazaUser.amount"
                :country="country"
                :is-out-of-stock="isSoldOut"
                :is-visible="isProductVisible"
                :is-loading="isProductLoading"
                :is-authenticated="isAuthenticated"
                @unauthenticatedProductClick="$emit('unauthenticatedProductClick')"
              />
            </div>
          </div>
          <div class="product-detail-description product-detail-description-formatting ota-p16 font-light mb-7" v-html="product.shortDescription" />
          <ProductColorPicker :options="product.colors" class="mb-6" />
          <OSelect
            class="size-select"
            :label="$t('Size')"
            :clearable="false"
            :options="productSizes"
            :placeholder="sizeSelectPlaceholder"
            :value="sizeSelectValue"
            required
            :selectable="selectable"
            @input="size = $event"
          />
          <div class="mt-15">
            <ProductCTA
              class="flex-1"
              :is-sold-out="isSoldOut || !!noticeBoxType"
              :is-wallet-frozen="isWalletFrozen"
              :is-product-visible="isProductVisible"
              :has-enough-credits="hasEnoughCredits"
              :should-remove-cart-items="(isAuthenticated && !!(cartTotal && !((spaazaUser.amount - cartTotal) >= product.price)))"
              :is-authenticated="isAuthenticated"
              @click="handleATC"
            />
            <div
              v-if="noticeBoxType"
              class="rounded bg-newRed-400 p-6 mt-6 text-neutral-00"
            >
              <h3 class="mb-5 ota-h3" v-text="$t(`Loyalty.Webshop.ProductDetail.PayWall.${noticeBoxType}.Title`)" />
              <p class="mb-5 ota-p16" v-html="$t(`Loyalty.Webshop.ProductDetail.PayWall.${noticeBoxType}.Description`, variables)" />
              <NewButton color="white" @click="() => onNoticeBoxClick(noticeBoxType)">
                <span class="font-semibold whitespace-no-wrap" v-text="$t(`Loyalty.Webshop.ProductDetail.PayWall.${noticeBoxType}.CTA`)" />
              </NewButton>
            </div>
          </div>
          <div class="pb-12">
            <div class="lg:hidden">
              <ProductAccordion class="mt-12">
                <template v-slot:trigger>
                  {{ $t('Loyalty.Webshop.ProductDetail.Description') }}
                </template>
                <template v-slot:content>
                  <div class="product-detail-description-formatting" v-html="product.longDescription" />
                </template>
              </ProductAccordion>
              <!-- <ProductAccordion class="mt-12">
                <template v-slot:trigger>
                  {{ $t('Loyalty.Webshop.ProductDetail.AdditionalInformation') }}
                </template>
                <template v-slot:content>
                  <div v-html="product.longDescription" />
                </template>
              </ProductAccordion> -->
            </div>
            <div class="hidden lg:block lg:mt-12">
              <ProductTabs>
                <ProductTab :title="$t('Loyalty.Webshop.ProductDetail.Description')">
                  <div class="ota-p16 font-light" v-html="product.longDescription" />
                </ProductTab>
                <ProductTab />
              </ProductTabs>
            </div>
          </div>
        </div>
      </div>
    </div>

    <NotifyMeModal ref="notifyMeModal" :type="notifyMeModalType" />
  </div>
</template>

<script>
import OSelect from '@/components/OSelect'
import BreadCrumbsComponent from '@/components/Webshop/BreadCrumbs'
import ProductColorPicker from '@/components/Webshop/ProductColorPicker'
import ProductAccordion from '@/components/Webshop/ProductAccordion'
import ProductPhotoGallery from '@/components/Webshop/ProductPhotoGallery'
import ProductTabs from '@/components/Webshop/ProductTabs'
import ProductTab from '@/components/Webshop/ProductTabs/ProductTab'
import ProductCreditBadge from '@/components/Webshop/ProductCreditBadge'
import ProductCTA from '@/components/Webshop/ProductCTA'
import NotifyMeModal from '@/components/Webshop/NotifyMeModal'
import NewButton from '@/components/NewButton'
import { notifyMeModalTypes } from '@/enums/webshop'
import {
  breadcrumbs,
  defaultEmptyString
} from '@/components/Webshop/props'
import { addLocalCurrency, getEuros } from '@/utils/general'
import { Currencies, ScreenBreakpoints } from '@/enums/general'
import { getOrderBundleName, getOrderBundleReleaseDate } from '@/utils/order'
import { vueWindowSizeMixin } from 'vue-window-size'

export default {
  name: 'ProductDetailPageComponent',
  components: {
    BreadCrumbsComponent,
    OSelect,
    ProductColorPicker,
    ProductAccordion,
    ProductPhotoGallery,
    ProductCreditBadge,
    ProductCTA,
    NotifyMeModal,
    ProductTabs,
    ProductTab,
    NewButton
  },
  mixins: [vueWindowSizeMixin],
  props: {
    breadcrumbs,
    category: defaultEmptyString,
    productSlug: defaultEmptyString,
    productTitle: defaultEmptyString,
    product: {
      type: Object,
      default: () => ({})
    },
    isProductLoading: {
      type: Boolean,
      default: false
    },
    isWalletFrozen: {
      type: Boolean,
      default: false
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    country: {
      type: String,
      default: 'nl'
    },
    locale: {
      type: String,
      default: 'nl'
    },
    spaazaUser: {
      type: Object,
      default: () => ({
        referralCode: '',
        currency: 'EUR',
        amount: 0
      })
    },
    cart: {
      type: Array,
      default: () => ([])
    },
    openPayments: {
      type: Array,
      default: null
    },
    cartTotal: {
      type: Number,
      default: 0
    },
    needsToUpdatePaymentMethod: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      size: {
        value: '',
        label: '',
        stock: '0'
      }
    }
  },
  computed: {
    isLargeScreen () {
      return this.windowWidth > ScreenBreakpoints.md
    },
    hasOpenPayment () {
      return !!this.openPayments.length > 0
    },
    openPaymentAmount () {
      if (!this.hasOpenPayment) {
        return 0
      }

      const amount = this.openPayments[0]?.orderBundle?.amount || 0

      return addLocalCurrency(amount, this.country, Currencies[this.country])
    },
    variables () {
      const amount = this.$t('Loyalty.Webshop.ProductDetail.PayWall.Amount', {
        amount: this.openPaymentAmount
      })

      const releaseDate = getOrderBundleReleaseDate(this.openPayments[0]?.orderBundle)

      let productInfo = `${getOrderBundleName(this.openPayments[0]?.orderBundle)}`

      if (releaseDate) {
        productInfo = `${productInfo} (${this.$t(`Months[${this.$moment(releaseDate).month()}]`)} ${this.$moment(releaseDate).format('YYYY')})`
      }

      const product = this.$t('Loyalty.Webshop.ProductDetail.PayWall.Product', {
        product: productInfo
      })

      return {
        amount,
        product
      }
    },
    noticeBoxType () {
      if (!this.userId) {
        return null
      }

      if (this.needsToUpdatePaymentMethod) {
        return 'NoPaymentContract'
      }

      if (this.hasOpenPayment) {
        return 'OpenPayment'
      }

      return null
    },
    productSizes () {
      if (!this.product.sizes) {
        return []
      }

      return this.product.sizes.map(sizeOption => ({
        ...sizeOption,
        ...(parseInt(sizeOption.stock) ? {} : { label: `${sizeOption.label} ${this.$t('Loyalty.Webshop.General.OutOfStock')}` })
      }))
    },
    hasEnoughCredits () {
      return !!((this.spaazaUser.amount - this.cartTotal) >= this.product.price)
    },
    sizeSelectValue () {
      if (!this.isProductVisible) {
        return null
      }

      return this.size.label || null
    },
    sizeSelectPlaceholder () {
      if (!this.isProductVisible) {
        return this.$t('Loyalty.Webshop.ProductDetail.ComingSoon')
      }

      if (this.isSoldOut) {
        return this.$t('Loyalty.Webshop.ProductDetail.ProductSoldOut')
      }

      return ''
    },
    isSoldOut () {
      return !this.product.sizes?.find(sizeOption => parseInt((sizeOption.stock)))
    },
    isProductVisible () {
      const releaseDate = this.$moment(this.product.visibleFrom)
      const now = this.$moment()

      return releaseDate.isBefore(now)
    },
    notifyMeModalType () {
      if (!this.isProductVisible) {
        return notifyMeModalTypes.comingSoon
      }

      return notifyMeModalTypes.soldOut
    }
  },
  watch: {
    'product.sizes': {
      handler (newSizes) {
        const firstInstockSize = newSizes?.find(sizeOption => parseInt((sizeOption.stock)))

        if (firstInstockSize) {
          this.size = firstInstockSize
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    toEuros (price = '0', keepZeros = false) {
      return getEuros(this.country, price, keepZeros)
    },
    onNoticeBoxClick (noticeBoxType = '') {
      if (noticeBoxType === 'OpenPayment') {
        this.$router.push(`${this.$i18n.localePath({ name: 'account-orders-id-pay', params: { id: this.openPayments[0].orderBundle._id } })}?next=${this.$route.fullPath}`)
        return
      }

      if (noticeBoxType === 'NoPaymentContract') {
        if (this.isLargeScreen) {
          this.$router.push(`${this.$i18n.localePath({ name: 'account-personal-details' })}?next=${this.$route.fullPath}#payment-details`)
          return
        }

        this.$router.push(`${this.$i18n.localePath({ name: 'account-personal-details-payment-details' })}?next=${this.$route.fullPath}`)
      }
    },
    selectable (option) {
      return !!(parseInt(option.stock) > 0)
    },
    myAddToCartFunction (slugWithSizeLabelPostFix) {
      const splits = slugWithSizeLabelPostFix.split('-') // turner-sweater-black-S
      const label = splits[splits.length - 1] // S

      this.setSizeByLabel(label)

      this.handleATC()
    },
    onWidgetResult (sizeLabelOrVariantId) {
      const label = sizeLabelOrVariantId.label || sizeLabelOrVariantId

      this.setSizeByLabel(label)
    },
    setSizeByLabel (label) {
      if (this.size.label !== label) {
        const size = this.productSizes.find(sizeOption => sizeOption.label === label)

        if (size) {
          this.size = size
        }
      }
    },
    handleATC () {
      if (!this.isAuthenticated) {
        this.$emit('unauthenticatedProductClick')
        return
      }

      if (this.isWalletFrozen) {
        this.$router.push(this.$i18n.localePath({ name: 'account-memberships-overview-status' }))

        return
      }

      if (this.isSoldOut || !this.isProductVisible) {
        this.$refs.notifyMeModal.toggleActivation(true)

        return
      }

      if (!this.hasEnoughCredits) {
        this.$otoaster.warning(this.$t('Loyalty.Webshop.ProductDetail.NotEnoughCredits'))
        return
      }

      this.$emit('addToCart', {
        price: this.product.price,
        name: this.product.name,
        category: this.product.category,
        sizeLabel: this.size.label,
        productId: this.size.value,
        slug: this.product.slug,
        quantity: 1,
        color: this.product.colorName
      })

      this.$otoaster.success(this.$t('Loyalty.Webshop.ProductDetail.AddedToCart'))
    },
    onBreadCrumbClick () {
      const fromParam = this.$route.query.from

      if (fromParam) {
        this.$router.push(this.$i18n.localePath({ name: fromParam }))
        return
      }

      this.$router.push(this.$i18n.localePath({ name: 'webshop' }))
    }
  }
}
</script>

<style lang="postcss" scoped>
.product-detail {
  &-container {
    .product-detail-description-formatting {
      >>> ul, ol {
        @apply list-inside list-disc;
      }

      >>> p, h1, h2, h3, h4, h5 {
        @apply mb-4;
      }
    }

    >>> .product-detail-description-formatting {
      ul, ol {
        @apply list-inside list-disc;
      }

      p, h1, h2, h3, h4, h5 {
        @apply mb-4;
      }
    }
  }

  &-title {
    font-size: 2.6rem;
    font-weight: 600;
    line-height: 2.1rem;
  }

  &-description {
    min-height: 2.4rem;
  }

  &-main-info {
    min-height: 11.6rem;
  }

  .size-select >>> input::placeholder {
    color: rgba(60,60,60,.5);
  }
}

@screen md {
  .product-detail {
    &-main-info {
      min-height: 11.8rem;
    }
  }
}

@screen lg {
  .product-detail-container {
    @apply flex;
  }

  .product-detail-body {
    @apply ml-auto;
    width: 100%;
    max-width: 45rem;
  }
}
</style>
